import { createPinia } from 'pinia'
import { createApp } from 'vue'

import App from './App.vue'
import router from './router'

import 'luxon'

import '@unocss/reset/tailwind-compat.css'
import 'virtual:uno.css'

// Animate.css (https://animate.style/)
import 'animate.css'

import '@/styles/index.scss'

import 'element-plus/theme-chalk/src/message-box.scss'
import 'element-plus/theme-chalk/src/message.scss'
import 'element-plus/theme-chalk/src/notification.scss'

import { VueQueryPlugin } from '@tanstack/vue-query'
import ElementPlus from 'element-plus'
import { authConfig } from './authConfig'
import { DEFAULT_CONFIG_RCAPP } from './config/rcapp.config'

// Plugins
import { AxiosPlugin } from './plugins/axios.plugin'
import { createUserManagerPlugin } from './plugins/usermanager.plugin'

// Sentry SDK
import * as Sentry from '@sentry/vue'
import type { AxiosError } from 'axios'

const app = createApp(App)

// Only instrument Sentry when NODE_ENV=production (includes staging and Vercel preview deployments)
if (import.meta.env.PROD) {
  Sentry.init({
    app,
    dsn: 'https://8a16d31cc52ec32aa4f6d6ae81ae6e88@o4509668001710080.ingest.us.sentry.io/4509991202324480',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [
      // send console.log, console.warn, and console.error calls as logs to Sentry
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    ],
    // Enable logs to be sent to Sentry
    enableLogs: true,
    environment: import.meta.env.MODE,
  })
}

app.provide('rcapp.config', DEFAULT_CONFIG_RCAPP)

app.use(router)
app.use(ElementPlus)
app.use(createPinia())
app.use(createUserManagerPlugin(authConfig))
app.use(AxiosPlugin)
app.use(VueQueryPlugin, {
  enableDevtoolsV6Plugin: true,
  queryClientConfig: {
    defaultOptions: {
      queries: {
        retry: (failureCount, error: Error) => {
          // Don't retry on 4XX status codes (client errors)
          const axiosError = error as AxiosError
          const status = axiosError.response?.status
          if (status && status >= 400 && status < 500) {
            return false
          }
          // Retry up to 3 times for other errors (5XX, network errors, etc.)
          return failureCount < 3
        },
        retryDelay: (attemptIndex) => {
          // Exponential backoff: starts at 1s, doubles each retry, capped at 30s
          // attemptIndex starts at 0 for the first retry
          return Math.min(1000 * 2 ** attemptIndex, 30000)
        },
      },
    },
  },
})

app.mount('#app')
